import type { QueryKey } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSettings } from '@wu/widget-core';
import type { Vote, VotesData } from '../../types';
import { unvoteLocal } from '../local-data';
import type { UnvoteResponse } from './unvote.query';
import { unvoteQuery } from './unvote.query';

export function useUnvoteMutation(queryKey: QueryKey) {
  const { widgetToken, answers } = useSettings();
  const queryClient = useQueryClient();

  return useMutation<
    UnvoteResponse['payload'],
    string,
    { id: Vote['id']; pid: string },
    { previousVotes?: VotesData }
  >({
    mutationFn: async ({ pid }) => await unvoteQuery(pid, widgetToken),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousVotes = queryClient.getQueryData<VotesData>(queryKey);

      if (previousVotes) {
        const newVotes = unvoteLocal({
          id,
          answers,
          data: previousVotes
        });

        queryClient.setQueryData(queryKey, newVotes);
      }

      return { previousVotes };
    },
    onError: (_, __, context) => {
      if (context && context.previousVotes) {
        queryClient.setQueryData(queryKey, context.previousVotes);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });
}
